// src/theme/theme.ts
// Tema central de MUI para SIF. Fuente de verdad de color, tipografia, densidad
// y defaults de componentes. Reemplaza los createTheme dispersos por componente.
//
// Paleta base: design system "Azul marino corporativo" (skill ui-ux-pro-max)
//   Primary navy #0F172A · Accent/CTA azul #0369A1 · fondo #F8FAFC / dark #020617
// Enfoque: accesible (contraste AA, foco visible), denso (ERP), sans-serif.
import { createTheme, Theme } from '@mui/material/styles';

// ── Tokens crudos ───────────────────────────────────────────────────────────
export const tokens = {
  light: {
    primary: '#0369A1',        // azul CTA interactivo (botones, enlaces)
    primaryDark: '#075985',
    primaryLight: '#0EA5E9',
    navy: '#0F172A',           // marca/encabezados/superficies oscuras (AppBar)
    secondary: '#334155',
    background: '#F8FAFC',
    paper: '#FFFFFF',
    text: '#0F172A',
    textSecondary: '#475569',
    border: '#E2E8F0',
    muted: '#E8ECF1',
    mutedText: '#475569',
    error: '#DC2626',
    warning: '#B45309',
    success: '#15803D',
    info: '#0369A1',
  },
  dark: {
    primary: '#38BDF8',        // azul mas claro para contraste sobre oscuro
    primaryDark: '#0EA5E9',
    primaryLight: '#7DD3FC',
    navy: '#0B1220',
    secondary: '#94A3B8',
    background: '#020617',
    paper: '#0F172A',
    text: '#E2E8F0',
    textSecondary: '#94A3B8',
    border: '#1E293B',
    muted: '#1E293B',
    mutedText: '#94A3B8',
    error: '#F87171',
    warning: '#FBBF24',
    success: '#4ADE80',
    info: '#38BDF8',
  },
} as const;

const FONT_STACK =
  '"Nunito", "Lato", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export function getTheme(mode: 'light' | 'dark'): Theme {
  const t = tokens[mode];
  const isLight = mode === 'light';

  return createTheme({
    palette: {
      mode,
      primary:   { main: t.primary, dark: t.primaryDark, light: t.primaryLight, contrastText: '#FFFFFF' },
      secondary: { main: t.secondary, contrastText: isLight ? '#FFFFFF' : '#0B1220' },
      error:     { main: t.error },
      warning:   { main: t.warning },
      success:   { main: t.success },
      info:      { main: t.info },
      background:{ default: t.background, paper: t.paper },
      text:      { primary: t.text, secondary: t.textSecondary },
      divider:   t.border,
    },

    shape: { borderRadius: 8 },

    // Tipografia sans con jerarquia clara (se descarta la serif sugerida por la skill:
    // no encaja en un ERP denso). Nunito ya se carga desde index.html.
    typography: {
      fontFamily: FONT_STACK,
      h1: { fontWeight: 800, fontSize: '2rem',    lineHeight: 1.2 },
      h2: { fontWeight: 800, fontSize: '1.6rem',  lineHeight: 1.25 },
      h3: { fontWeight: 700, fontSize: '1.35rem', lineHeight: 1.3 },
      h4: { fontWeight: 700, fontSize: '1.15rem', lineHeight: 1.35 },
      h5: { fontWeight: 700, fontSize: '1rem',    lineHeight: 1.4 },
      h6: { fontWeight: 700, fontSize: '0.9rem',  lineHeight: 1.4 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 700, textTransform: 'none', letterSpacing: 0 },
      body1: { fontSize: '0.925rem' },
      body2: { fontSize: '0.85rem' },
    },

    components: {
      // Foco visible global (accesibilidad AA por teclado) + tipografia base
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: t.background, color: t.text },
          '*:focus-visible': {
            outline: `2px solid ${t.primary}`,
            outlineOffset: '2px',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            paddingInline: 16,
            transition: 'background-color .2s ease, box-shadow .2s ease',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 8, backgroundColor: t.paper } },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${t.border}`,
            borderRadius: 12,
            backgroundImage: 'none',
            boxShadow: isLight
              ? '0 1px 2px rgba(15,23,42,.06), 0 1px 3px rgba(15,23,42,.04)'
              : '0 1px 2px rgba(0,0,0,.5)',
          },
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      // Densidad de tablas (ERP): celdas mas compactas y legibles
      MuiTableCell: {
        styleOverrides: {
          root: { paddingBlock: 8, paddingInline: 12, borderColor: t.border },
          head: { fontWeight: 700, backgroundColor: t.muted, color: t.text },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { fontSize: '0.75rem', backgroundColor: t.navy },
        },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 6, fontWeight: 600 } } },
    },
  });
}

export const lightTheme = getTheme('light');
export const darkTheme = getTheme('dark');
